import { darkColors, Modal } from '@tryhackme/thm-ui-components';
import { type FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SupportedLanguage, useIsFrench } from 'src/common/contexts/language-context';
import { useGetUserQuery } from 'src/common/slices';
import { useGetInfluencersCampaignsQuery } from 'src/features/admin/campaigns/campaigns.slice';
import redditImage from 'src/images/pngs/campaigns/reddit.png';
import { ThemeProvider, useTheme } from 'styled-components';
import { useActiveCampaign } from '../../../hooks/use-active-campaign';
import { useCampaignCta } from '../../../hooks/use-campaign-cta';
import { useCampaignStorage } from '../../../hooks/use-campaign-storage';
import { useInfluencerConfigs } from '../../hooks/use-influencer-additional-configs';
import { useInfluencerSubscriptionCampaign } from '../../hooks/use-influencer-campaign';
import { InfluencerModalSvg } from '../modal-content/influencer-modal-svg';
import { StyledContent, StyledInfluencerImage, StyledInfluencerRedirectButton, StyledWrapper } from './influencer-subscription-campaign-modal.styles';
import type { InfluencerSubscriptionCampaignModalProps } from './influencer-subscription-campaign-modal.types';
export const InfluencerSubscriptionCampaignModal: FC<InfluencerSubscriptionCampaignModalProps> = ({
  influencerCampaign,
  content: Content,
  hideInfluencerDetails = false
}) => {
  const theme = useTheme();
  const {
    data: userDataResponse
  } = useGetUserQuery();
  const {
    campaign
  } = useActiveCampaign();
  const isUserPreferenceFrench = useIsFrench();
  const campaignLanguage = isUserPreferenceFrench ? SupportedLanguage.FRENCH : SupportedLanguage.ENGLISH;
  const [ctaText, loggedUserUrlToNavigate] = useCampaignCta(campaignLanguage);
  const {
    isSeen,
    markAsSeen,
    reset
  } = useCampaignStorage(campaign);
  const [prevAuthState, setPrevAuthState] = useState(!!userDataResponse?.data);
  const [open, setOpen] = useState(!userDataResponse?.data || !isSeen);
  const {
    data: campaigns
  } = useGetInfluencersCampaignsQuery({
    page: 1,
    limit: 1,
    name: influencerCampaign
  });
  const campaignData = campaigns?.data?.docs[0];
  const campaignConfigs = useInfluencerSubscriptionCampaign(influencerCampaign, campaignData);
  const additionalConfigs = useInfluencerConfigs(campaignData);
  useEffect(() => {
    if (isSeen) {
      setOpen(false);
    }
  }, [isSeen]);
  useEffect(() => {
    const isLoggedIn = !!userDataResponse?.data;
    const wasLoggedIn = !!prevAuthState;
    if (isLoggedIn !== wasLoggedIn) {
      reset();
      setPrevAuthState(isLoggedIn);
    }
  }, [userDataResponse?.data, prevAuthState, reset]);
  const onOpenChange = (isOpen: boolean) => {
    if (isOpen) return;
    markAsSeen();
    setOpen(false);
  };
  if (!additionalConfigs || !campaignConfigs) return null;
  const {
    icon,
    urlToNavigate
  } = additionalConfigs;
  const {
    userFriendlyName
  } = campaignConfigs;
  const url = userDataResponse?.data ? loggedUserUrlToNavigate : urlToNavigate;
  const isRedditCampaign = influencerCampaign === 'REDDIT_2025';
  return <ThemeProvider theme={{
    ...theme,
    colors: darkColors
  }} data-sentry-element="ThemeProvider" data-sentry-component="InfluencerSubscriptionCampaignModal" data-sentry-source-file="influencer-subscription-campaign-modal.tsx">
      <Modal padding="0" open={open} onOpenChange={onOpenChange} data-sentry-element="Modal" data-sentry-source-file="influencer-subscription-campaign-modal.tsx">
        <StyledWrapper data-sentry-element="StyledWrapper" data-sentry-source-file="influencer-subscription-campaign-modal.tsx">
          <StyledContent data-sentry-element="StyledContent" data-sentry-source-file="influencer-subscription-campaign-modal.tsx">
            {!hideInfluencerDetails && icon && <InfluencerModalSvg icon={icon} />}
            {hideInfluencerDetails && !isRedditCampaign && icon && <StyledInfluencerImage src={icon} width={210} height={210} />}
            {hideInfluencerDetails && isRedditCampaign && icon && <StyledInfluencerImage src={redditImage} width={240} height={190} />}

            <Content campaign={campaign} userFriendlyName={userFriendlyName} language={campaignLanguage} hideInfluencerDetails={hideInfluencerDetails} data-sentry-element="Content" data-sentry-source-file="influencer-subscription-campaign-modal.tsx" />

            <StyledInfluencerRedirectButton as={Link} to={url ?? ''} role="button" variant="solid" color="primary" onClick={markAsSeen} data-sentry-element="StyledInfluencerRedirectButton" data-sentry-source-file="influencer-subscription-campaign-modal.tsx">
              {ctaText}
            </StyledInfluencerRedirectButton>
          </StyledContent>
        </StyledWrapper>
      </Modal>
    </ThemeProvider>;
};